<template>
  <form @submit.prevent="$emit('onSubmit')">
    <BasicOverview
      :booking="booking"
      :hide-price="invoicingPolicy.hidePrices"
      :hide-price-text="t(invoicingPolicy.hidePricesText ?? '')"
      @has-changed="(changed) => (hasChanged.basicOverview = changed)"
    />

    <Divider />

    <div class="grid gap-4 md:grid-cols-3">
      <div class="col-span-2">
        <VehicleImmutable v-model:car="allocation.car" :booking-id="booking.id" />
      </div>
    </div>

    <Divider />

    <div class="relative mt-4 grid gap-4 md:grid-cols-3">
      <TimeRange
        :hide-booking-range-picker="!invoicingPolicy.canChangeRange || !canChangeRange"
        :booking="booking"
        :disabled="disablePriceChangingModules('rangeAndLocation')"
        handover-adjustment
        show-locations
        @has-changed="(changed) => (hasChanged.rangeAndLocation = changed)"
      />

      <RangeInfos :booking="booking" immutable />
    </div>

    <template v-if="!isLongTerm">
      <Divider />

      <UpgradeExtras
        v-if="!invoicingPolicy.extrasImmutable && !showAdvancedExtraSelection"
        :booking="booking"
        :disabled="disablePriceChangingModules('extras')"
        handover-adjustment
        @has-changed="(changed) => (hasChanged.extras = changed)"
      />

      <Extras
        v-if="invoicingPolicy.extrasImmutable || showAdvancedExtraSelection"
        :booking="booking"
        :immutable="invoicingPolicy.extrasImmutable"
        :price-difference-counterparty="CounterpartyType.CUSTOMER"
        handover-adjustment
        :disabled="disablePriceChangingModules('extras')"
        @has-changed="(changed) => (hasChanged.extras = changed)"
      />

      <div v-if="!invoicingPolicy.extrasImmutable" class="flexjustify-end mt-2">
        <CVButton
          variant="link"
          class="max-sm:w-full"
          @click.prevent="showAdvancedExtraSelection = !showAdvancedExtraSelection"
        >
          {{ !showAdvancedExtraSelection ? t('editExtras') : t('showUpgrades') }}
        </CVButton>
      </div>
    </template>

    <Divider />

    <div v-if="!isLongTerm && customBookedExtras.length">
      <CustomExtrasImmutable
        :custom-booked-extras="customBookedExtras"
        :hide-price="invoicingPolicy.hidePrices"
        :hide-price-text="t(invoicingPolicy.hidePricesText ?? '')"
      />

      <Divider />
    </div>

    <div v-if="booking.insuranceCase?.id">
      <InsuranceCaseForm
        ref="insuranceData"
        :booking="booking"
        @valid="isInsuranceDataValid = $event"
      />
      <Divider />
    </div>

    <div class="flex items-end gap-y-3 max-sm:flex-col max-sm:items-stretch">
      <CancelButton
        v-if="
          $can(
            'update',
            subject('Booking', { ...booking, status: BookingStatus.CANCELED }),
            'status',
          )
        "
        :booking="booking"
        :hide-revert-cancelling="true"
        class="flex-1"
        @booking-cancelled="$router.push({ name: 'upcomingHandovers' })"
      />
      <div class="flex flex-1 items-end justify-end gap-8 max-sm:flex-col max-sm:items-stretch">
        <p class="justify-self-end text-right">
          {{ isLongTerm ? t('currentPeriodPrice') : t('totalPrice') }}
          <span
            v-if="!invoicingPolicy.hidePrices"
            v-currency="handoverQuotePrice"
            class="block text-base font-semibold md:text-lg"
          />
          <span v-else class="block">{{ t(invoicingPolicy.hidePricesText ?? '') }}</span>
        </p>
        <ButtonNext
          :disabled="hasChanged.extras || !isInsuranceDataValid"
          class="justify-self-end"
          type="submit"
        >
          {{ t('next') }}
        </ButtonNext>
      </div>
    </div>
  </form>
</template>

<script lang="ts" setup>
import BasicOverview from '@/components/booking-forms/BasicOverview.vue';
import Extras from '@/components/booking-forms/Extras.vue';
import TimeRange from '@/components/booking-forms/TimeRange.vue';
import RangeInfos from '@/components/booking-forms/RangeInfos.vue';
import VehicleImmutable from '@/components/booking-forms/VehicleImmutable.vue';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import Divider from '@/components/Divider.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import type {
  GetAllVehicleTypesCarsDto,
  GetAllVehicleTypesDto,
} from '@/entities/vehicle-type.entity';
import { useI18n } from 'vue-i18n';
import { computed, reactive, ref } from 'vue';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import CustomExtrasImmutable from '@/components/booking-forms/CustomExtrasImmutable.vue';
import UpgradeExtras from '@/components/booking-forms/UpgradeExtras.vue';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/vue';
import CancelButton from '@/views/bookings/booking-edit/components/CancelButton.vue';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import { CounterpartyType } from '@carvia/ros-client-types';
import InsuranceCaseForm from '@/views/handover/components/overview/InsuranceCaseForm.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { getInvoicingPolicy } from '@/utils/invoicing-policy.ts';
import {
  bookingStartTimezone,
  getCustomBookedExtras,
  getCurrentPricingPeriod,
  getPeriodPrice,
  isLongTermPricing,
} from '@/utils/pricing.utils';

export type CarWithPricing = GetAllVehicleTypesCarsDto & {
  vehicleType: GetAllVehicleTypesDto;
};

const { t } = useI18n();
const props = defineProps<{
  booking: Booking;
}>();
defineEmits<{
  (e: 'onSubmit'): void;
}>();

const allocation = computed(() => props.booking.allocations[0]);
const customBookedExtras = computed(() =>
  getCustomBookedExtras(props.booking.priceCalculation, bookingStartTimezone(props.booking)),
);

const showAdvancedExtraSelection = ref(false);

const hasChanged = reactive({
  rangeAndLocation: false,
  extras: false,
  basicOverview: false,
});

const isInsuranceDataValid = ref(!props.booking.insuranceCase?.id);

const invoicingPolicy = computed(() => getInvoicingPolicy(props.booking));
// External drivers adjust extras but leave the booking range to the station (ADR 0021).
const ability = useAbility();
const canChangeRange = computed(() =>
  ability.can('update', subject('Booking', props.booking), 'startDate'),
);
const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));
const handoverQuotePrice = computed(() => {
  const pricing = props.booking.priceCalculation;
  if (!isLongTermPricing(pricing)) {
    return pricing.totalPrice;
  }
  const period = getCurrentPricingPeriod(pricing, new Date(), bookingStartTimezone(props.booking));
  return period ? getPeriodPrice(period) : 0;
});

const disablePriceChangingModules = (ignore: 'extras' | 'rangeAndLocation') =>
  Object.entries({
    extras: hasChanged.extras,
    rangeAndLocation: hasChanged.rangeAndLocation,
  }).some(([key, value]) => key !== ignore && value);

usePreventLeavingUnsavedForm(() => Object.values(hasChanged).some((changed) => changed));
</script>

<i18n lang="json">
{
  "en": {
    "totalPrice": "Total Price",
    "currentPeriodPrice": "Current period price",
    "backToOverview": "Back to Handoverlist",
    "noOption": "No",
    "totalBasePrice": "Car Price (without Extras)",
    "paidByAgency": "Paid by agency",
    "paidByInsurance": "Paid by insurance",
    "editExtras": "Edit Extras",
    "showUpgrades": "Show Upgrades"
  },
  "de": {
    "totalPrice": "Gesamtpreis",
    "currentPeriodPrice": "Preis des aktuellen Zeitraums",
    "backToOverview": "Zurück zur Übergabeliste",
    "noOption": "Kein",
    "totalBasePrice": "Fahrzeugpreis (ohne Extras)",
    "paidByAgency": "Von Vermittler bezahlt",
    "paidByInsurance": "Von Versicherung bezahlt",
    "editExtras": "Extras bearbeiten",
    "showUpgrades": "Upgrades anzeigen"
  },
  "es": {
    "totalPrice": "Precio total",
    "currentPeriodPrice": "Precio del periodo actual",
    "backToOverview": "Volver a la lista de entregas",
    "noOption": "Ninguno",
    "totalBasePrice": "Precio del vehículo (sin extras)",
    "paidByAgency": "Pagado por la agencia",
    "paidByInsurance": "Pagado por el seguro",
    "editExtras": "Editar extras",
    "showUpgrades": "Mostrar mejoras"
  }
}
</i18n>
